'use client';

import { Heart, PlusIcon, CheckIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '~/lib/utils';
import { trpc } from '~/trpc/client';
import { useEffect, useRef, useState } from 'react';
import { useFavourites } from '~/context/favourites-context';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Tooltipper } from '../common/tooltipper';
import { Skeleton } from '../ui/skeleton';
import { Input } from '../ui/input';

type ProductsILoveButtonProps = {
  className?: string;
  catRef: string;
  variant: 'main' | 'alternate';
  strokeWidth?: number;
  showMyLists?: boolean;
  isInitialFavourite?: boolean | null;
};

export type ListDetails = {
  id: string;
  name: string;
  productDetails: MyListProductDetails;
};

type MyListProductDetails = {
  listItemId: number;
  listId: string;
};

export function ProductsILoveButton({
  className,
  catRef,
  variant,
  strokeWidth,
  showMyLists = false,
  isInitialFavourite = false,
}: ProductsILoveButtonProps) {
  const favouritesContext = useFavourites();
  const [creatingNewList, setCreatingNewList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const onScroll = () => {
      // You can tweak threshold as needed
      setScrolled(el.scrollTop > 0);
    };

    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (creatingNewList) {
      inputRef.current?.focus();
    }
  }, [creatingNewList]);

  const setProductsILove =
    trpc.productsILoveRouter.setProductsILove.useMutation({
      onSuccess() {
        if (!favouritesContext.isInProductsILove({ catalogueRef: catRef })) {
          toast.success('Item has been added to Products I love.');
          favouritesContext.addToFavourites(catRef);
        } else {
          toast.success('Item has been removed from Products I love.');
          favouritesContext.removeFromFavourites(catRef);
        }

        return;
      },

      onError() {
        return;
      },
    });

  const addToMyList = trpc.myLists.addToMyList.useMutation({
    onSuccess(data, variables) {
      if (data.success) {
        variables.forEach((item) => {
          if (item.myListId && item.catalogueRef) {
            favouritesContext.addToMyList({
              catalogueRef: item.catalogueRef,
              myListId: item.myListId,
              newListName: null,
              isNewList: false,
            });
            const listName = favouritesContext.myLists.find(
              (list) => list.listId === item.myListId,
            )?.listName;
            toast.success(`Item has been added to ${listName}.`);
          } else if (item.isNewList && item.newListName) {
            const newListId = data.newLists.find(
              (list) => list.name === item.newListName,
            )?.myListId;
            favouritesContext.addToMyList({
              catalogueRef: item.catalogueRef,
              myListId: newListId ?? 0,
              newListName: item.newListName,
              isNewList: true,
            });
            toast.success(
              `New List is created and item added to ${item.newListName}.`,
            );
            // Reset state
            setCreatingNewList(false);
            setNewListName('');
          }
        });
      } else toast.error('Failed to add item to My List.');
    },
    onError(error) {
      if (error.data?.code === 'UNAUTHORIZED') {
        toast.error('Please sign in to add items to your list.');
      } else {
        toast.error('Something went wrong. Failed to add items to My List.');
      }
    },
  });

  const deleteMyListItems = trpc.myLists.deleteMyListItems.useMutation({
    onSuccess(data, variables) {
      if (data.success) {
        variables.forEach((item) => {
          favouritesContext.deleteFromMyList({
            catalogueRef: item.catalogueRef,
            myListId: item.myListId,
          });
          const listName = favouritesContext.myLists.find(
            (list) => list.listId === item.myListId,
          )?.listName;
          toast.success(`Item has been removed from ${listName}.`);
        });
      } else {
        data.deletedItems.forEach((item) => {
          favouritesContext.deleteFromMyList({
            catalogueRef: catRef,
            myListId: item.myListId,
          });
          const listName = favouritesContext.myLists.find(
            (list) => list.listId === item.myListId,
          )?.listName;
          toast.success(`Item has been removed from ${listName}.`);
        });
        data.deleteFailedItems.forEach((item) => {
          const listName = favouritesContext.myLists.find(
            (list) => list.listId === item.myListId,
          )?.listName;
          toast.success(`Failed to remove from ${listName}.`);
        });
      }
    },
    onError(error) {
      if (error.data?.code === 'UNAUTHORIZED') {
        toast.error('Please sign in to remove items from your list.');
      } else {
        toast.error('Failed to delete item from My List.');
      }
    },
  });

  function handleCreateList() {
    if (!newListName.trim()) {
      toast.error('Please enter a valid list name.');
      return;
    }

    void addToMyList.mutateAsync([
      {
        catalogueRef: catRef,
        quantity: 1,
        newListName,
        isNewList: true,
        setNewListDefault: false,
      },
    ]);
  }

  const isInAnyList = favouritesContext.isInAnyList({ catalogueRef: catRef });
  const isInProductsILove = favouritesContext.isInProductsILove({
    catalogueRef: catRef,
  });

  if (showMyLists) {
    return (
      <div
        className={cn(
          className,
          variant == 'main' ? 'text-[--pil-main]' : 'text-[--pil-alternate]',
        )}
      >
        <DropdownMenu
          onOpenChange={(setOpen) => {
            if (setOpen) {
              if (!isInProductsILove && !isInAnyList) {
                // If the dropdown is opened and the product is not in favourites or my lists, we
                //add
                void setProductsILove.mutateAsync({
                  catalogRef: catRef,
                  isRemove: false,
                });
              }
            } else {
              // If the dropdown is closed, we can reset the state
              setCreatingNewList(false);
              setNewListName('');
            }
          }}
        >
          <Tooltipper content="Favourites">
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="products-i-love-button"
                variant="custom"
                size="custom"
                className={cn(
                  'h-full w-full border-none stroke-[1.5] hover:fill-current',
                  isInProductsILove ? 'fill-current' : 'fill-white',
                )}
              >
                <Heart
                  className={cn(
                    'h-full w-full stroke-[1.5] hover:fill-current',
                    favouritesContext.isFavouritesLoading
                      ? isInitialFavourite
                        ? 'fill-current'
                        : 'fill-white'
                      : isInProductsILove || isInAnyList
                        ? 'fill-current'
                        : 'fill-white',
                  )}
                  strokeWidth={strokeWidth}
                />
              </Button>
            </DropdownMenuTrigger>
          </Tooltipper>
          <DropdownMenuContent className="absolute !right-[-50px] mt-5 w-[200px] max-w-64 !overflow-visible border-2 border-solid border-black shadow-md sm:w-64">
            <div className="relative px-2">
              <div className="absolute right-[28px] top-[-21px] h-8 w-8 rotate-45 border-l-2 border-t-2 border-solid border-black bg-white" />

              <div
                ref={scrollContainerRef}
                className="max-h-52 overflow-y-auto"
              >
                <div className="grid w-full items-center bg-white pt-1.5">
                  <DropdownMenuItem
                    className="flex w-full items-center px-1 pr-2 sm:pr-0 sm:text-base"
                    asChild
                    onClick={(e) => e.preventDefault()}
                  >
                    <button
                      data-testid="products-i-love-button"
                      className={cn(
                        'justify-between gap-2 border-none px-0 py-0 focus-visible:border-none focus-visible:ring-0 focus-visible:ring-offset-0',
                        scrolled ? 'mr-[calc(checkBoxWidth+gap)]' : '',
                      )}
                      // className=""
                      disabled={setProductsILove.isPending}
                      onClick={() => {
                        if (
                          favouritesContext.isInProductsILove({
                            catalogueRef: catRef,
                          })
                        ) {
                          //remove
                          void setProductsILove.mutateAsync({
                            catalogRef: catRef,
                            isRemove: true,
                          });
                        } else {
                          //add
                          void setProductsILove.mutateAsync({
                            catalogRef: catRef,
                            isRemove: false,
                          });
                        }
                      }}
                    >
                      <Heart
                        className={cn(
                          'h-4 w-4 fill-current stroke-[1.5] hover:fill-current',
                          variant == 'main'
                            ? 'text-[--pil-main]'
                            : 'text-[--pil-alternate]',
                        )}
                        strokeWidth={strokeWidth}
                      />
                      <span className="font-bold">Products I Love</span>
                      <span className="ml-auto flex h-4 w-4 items-center justify-center rounded-sm border-2 border-solid border-black sm:h-5 sm:w-5">
                        {favouritesContext.isFavouritesLoading
                          ? isInitialFavourite && (
                              <CheckIcon
                                className="h-3 w-3 sm:h-4 sm:w-4"
                                strokeWidth={3}
                              />
                            )
                          : isInProductsILove && (
                              <CheckIcon
                                className="h-3 w-3 sm:h-4 sm:w-4"
                                strokeWidth={3}
                              />
                            )}
                      </span>
                    </button>
                  </DropdownMenuItem>
                </div>
                <DropdownMenuSeparator className="mx-1 bg-[#9B9B9B]" />

                {favouritesContext.isMyListsLoading ? (
                  <div className="my-4 grid w-full gap-2 px-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-4 w-full" />
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="px-1 py-2 text-start text-xs font-bold sm:text-base">
                      My Lists:
                    </div>

                    {favouritesContext.myLists.map((list, index) => (
                      <DropdownMenuItem
                        key={index}
                        asChild
                        className="flex w-full cursor-pointer items-center gap-4 px-1 pr-2 sm:pr-0 sm:text-base"
                        onClick={(e) => e.preventDefault()}
                        disabled={
                          addToMyList.isPending || deleteMyListItems.isPending
                        }
                      >
                        <button
                          className="flex- flex-row justify-between border-none px-0 py-0 focus:rounded-none focus-visible:border-none focus-visible:ring-0 focus-visible:ring-offset-0"
                          onClick={() => {
                            const isInList = favouritesContext.isInMyList({
                              catalogueRef: catRef,
                              myListId: list.listId,
                            });

                            if (isInList) {
                              void deleteMyListItems.mutateAsync([
                                {
                                  catalogueRef: catRef,
                                  myListId: list.listId,
                                  myListItemId: null,
                                },
                              ]);
                            } else {
                              void addToMyList.mutateAsync([
                                {
                                  catalogueRef: catRef,
                                  myListId: list.listId,
                                  newListName: null,
                                  isNewList: false,
                                  quantity: 1,
                                  setNewListDefault: null,
                                },
                              ]);
                            }
                          }}
                        >
                          <Tooltipper content={list.listName}>
                            <span className="block w-10/12 max-w-max truncate text-xs font-normal sm:w-11/12 sm:text-base">
                              {list.listName}
                            </span>
                          </Tooltipper>
                          <span className="ml-auto flex h-4 w-4 items-center justify-center rounded-sm border-2 border-solid border-black sm:h-5 sm:w-5">
                            {favouritesContext.isInMyList({
                              catalogueRef: catRef,
                              myListId: list.listId,
                            }) && (
                              <CheckIcon
                                className="h-3 w-3 sm:h-4 sm:w-4"
                                strokeWidth={3}
                              />
                            )}
                          </span>
                        </button>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </div>

              <div className="mt-2 flex w-full items-center justify-between px-1">
                <div className="flex w-full items-center">
                  {creatingNewList ? (
                    <div className="just-between flex flex-row items-center">
                      <Input
                        ref={inputRef}
                        value={newListName}
                        onChange={(e) => setNewListName(e.target.value)}
                        placeholder="Enter List name"
                        className="h-6 grow border-primary py-0 pl-1 sm:h-8"
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') {
                            handleCreateList();
                          }
                        }}
                        maxLength={50}
                        disabled={addToMyList.isPending}
                      />
                      <CheckIcon
                        className="ml-2 h-4 w-4 cursor-pointer text-success sm:h-6 sm:w-6"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCreateList();
                        }}
                      />
                    </div>
                  ) : (
                    <DropdownMenuItem
                      className="flex w-full cursor-pointer items-center gap-4 px-1 text-xs font-normal sm:text-base"
                      onSelect={(e) => {
                        e.preventDefault(); // prevent menu from closing
                        setCreatingNewList(true);
                      }}
                    >
                      <div className="mr-4 flex cursor-pointer items-center gap-2 rounded-sm border border-gray-400 px-2 font-normal sm:text-base">
                        <PlusIcon className="mr-2 h-4 w-4 sm:h-6 sm:w-6" />
                        <span className="text-xs font-normal sm:text-base">
                          Create a new list
                        </span>
                      </div>
                    </DropdownMenuItem>
                  )}
                </div>
              </div>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div
      className={cn(
        className,
        variant == 'main' ? 'text-[--pil-main]' : 'text-[--pil-alternate]',
      )}
    >
      <Button
        data-testid="products-i-love-button"
        variant="custom"
        size="custom"
        className="h-full w-full border-none"
        onClick={(e) => {
          if (favouritesContext.isInProductsILove({ catalogueRef: catRef })) {
            //remove
            void setProductsILove.mutateAsync({
              catalogRef: catRef,
              isRemove: true,
            });
          } else {
            //add
            void setProductsILove.mutateAsync({
              catalogRef: catRef,
              isRemove: false,
            });
          }

          e.stopPropagation();
          return;
        }}
      >
        <Heart
          className={cn(
            'h-full w-full stroke-[1.5] hover:fill-current',
            favouritesContext.isFavouritesLoading
              ? isInitialFavourite
                ? 'fill-current'
                : 'fill-white'
              : isInProductsILove
                ? 'fill-current'
                : 'fill-white',
          )}
          strokeWidth={strokeWidth}
        />
      </Button>
    </div>
  );
}
